import React from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Search } from "../../components/Search";
import ProvidersFilter from "../../components/ProvidersFilter";
import PatientsFilter from "../../components/PatientsFilter";
import { Provider } from "../../types/api";
import { Patient } from "../../services/patientsService";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/selectors";
import { ASSISTANT_ROLES } from "../../constants";

interface SessionsHeaderProps {
  searchQuery: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  providers: Provider[];
  selectedProviders: number[];
  onProvidersChange: (providers: number[]) => void;
  onProvidersFilterOpen?: () => void;
  onProvidersFilterClose?: () => void;
  providersLoading?: boolean;
  patients: Patient[];
  selectedPatients: number[];
  onPatientsChange: (patientIds: number[]) => void;
  onPatientsFilterOpen?: () => void;
  onPatientsFilterClose?: () => void;
  patientsLoading?: boolean;
}

const SessionsHeader: React.FC<SessionsHeaderProps> = ({
  searchQuery,
  onSearchChange,
  providers,
  selectedProviders,
  onProvidersChange,
  onProvidersFilterOpen,
  onProvidersFilterClose,
  providersLoading = false,
  patients,
  selectedPatients,
  onPatientsChange,
  onPatientsFilterOpen,
  onPatientsFilterClose,
  patientsLoading = false,
}) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);

  const isManagerOrAssistantRole = ASSISTANT_ROLES.includes(
    user?.roleTitle as (typeof ASSISTANT_ROLES)[number],
  );

  return (
    <Box className="content-header">
      <Typography variant="h1">{t("allSessions.title")}</Typography>
      <Box sx={{ display: "flex", gap: 2, alignItems: "end" }}>
        <Search
          width={isManagerOrAssistantRole ? 257 : 320}
          value={searchQuery}
          onChange={onSearchChange}
          placeholder={t("allSessions.searchPlaceholder")}
        />
        {isManagerOrAssistantRole && (
          <ProvidersFilter
            width={257}
            providers={providers}
            selectedProviders={selectedProviders}
            onProvidersChange={onProvidersChange}
            onOpen={onProvidersFilterOpen}
            onClose={onProvidersFilterClose}
            loading={providersLoading}
          />
        )}
        <PatientsFilter
          width={257}
          patients={patients}
          selectedPatients={selectedPatients}
          onPatientsChange={onPatientsChange}
          onOpen={onPatientsFilterOpen}
          onClose={onPatientsFilterClose}
          loading={patientsLoading}
        />
      </Box>
    </Box>
  );
};

export default React.memo(SessionsHeader);
